"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Mist() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: diagramRef,
    isVisible: diagramVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const callouts = [{
    label: "GENTLE DIFFUSION",
    top: "12%",
    left: "55%"
  }, {
    label: "pH BALANCED",
    top: "42%",
    left: "58%"
  }, {
    label: "NON-TOXIC BASE",
    top: "70%",
    left: "55%"
  }, {
    label: "INSTANT ABSORPTION",
    top: "85%",
    left: "20%"
  }];
  const stats = [{
    value: "100%",
    label: "PLANT-BASED",
    descriptor: "Pure, botanical ingredients chosen for safety and comfort in every family environment."
  }, {
    value: "0%",
    label: "HARSH ALCOHOL",
    descriptor: "A sting-free, soothing mist that protects delicate skin without any aggressive chemicals."
  }, {
    value: "360°",
    label: "FULL COVERAGE",
    descriptor: "Whisper-quiet diffusion that wraps every surface in a fine, protective cloud of care."
  }, {
    value: "24/7",
    label: "READY TO USE",
    descriptor: "Your portable companion for daily transitions, making every entry feel safe and welcoming."
  }];
  return <section data-section-id="3634" id="why-mist-instead-of-spray" className="dark relative bg-cream-warm text-sage-deep overflow-hidden py-24 md:py-40 px-6 md:px-10 lg:px-16">
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-10" style={{
      backgroundImage: "linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[45fr_55fr] gap-16 lg:gap-20 items-center">
          <div ref={diagramRef} data-index={0} className={`relative aspect-[3/4] w-full transition-all duration-1000 ease-out ${diagramVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <svg viewBox="0 0 400 540" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-label="B.Y.E. Hygiene Mist bottle with gentle misting technology illustration, soft product photography">
              <rect x="120" y="500" width="160" height="20" stroke="currentColor" strokeWidth="1" />
              <line x1="100" y1="520" x2="300" y2="520" stroke="currentColor" strokeWidth="1" />

              <rect x="160" y="80" width="80" height="420" rx="24" stroke="currentColor" strokeWidth="1" />
              <line x1="160" y1="80" x2="160" y2="500" stroke="currentColor" strokeWidth="1" />
              <line x1="240" y1="80" x2="240" y2="500" stroke="currentColor" strokeWidth="1" />

              {[120, 180, 240, 300, 360, 420].map((y, i) => <g key={i}>
                  <line x1="160" y1={y} x2="240" y2={y} stroke="currentColor" strokeWidth="0.5" />
                  <circle cx="155" cy={y} r="3" stroke="currentColor" strokeWidth="0.5" fill="none" />
                  <circle cx="245" cy={y} r="3" stroke="currentColor" strokeWidth="0.5" fill="none" />
                </g>)}

              {[100, 160, 220, 280, 340, 400, 460].map((y, i) => <g key={`mist-${i}`}>
                  <circle cx="200" cy={y} r="4" fill="var(--primary)" />
                  <circle cx="200" cy={y} r="8" stroke="var(--primary)" strokeWidth="0.5" fill="none" opacity="0.5" />
                </g>)}

              <path d="M 200 80 L 200 500" stroke="var(--primary)" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.9" />

              <rect x="140" y="50" width="120" height="30" rx="8" stroke="currentColor" strokeWidth="1" />
              <rect x="140" y="500" width="120" height="15" rx="4" stroke="var(--primary)" strokeWidth="1" />
            </svg>

            {callouts.map((c, i) => <div key={i} data-index={i} className="absolute font-mono text-[10px] md:text-xs uppercase tracking-widest text-sage-deep/80 pointer-events-none" style={{
            top: c.top,
            left: c.left
          }}>
                <span className="inline-block border-l border-primary pl-2">{c.label}</span>
              </div>)}
          </div>

          <div>
            <div ref={headerRef} className={`transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-sage-deep/60 mb-8">
                04 / The Ritual
              </div>

              <h2 className="font-default font-medium uppercase leading-[0.95] tracking-tight text-5xl md:text-6xl lg:text-[5rem]">
                <span className="block text-sage-deep">Why Mist <span className="text-primary italic">Instead of Spray</span></span>
              </h2>

              <p className="font-default text-base md:text-lg leading-relaxed text-sage-deep/80 mt-10 max-w-xl">
                Traditional sprays often feel aggressive and clinical. B.Y.E. Hygiene Mist™ transforms 
                protection into a soft, intentional ritual that is as effective as it is effortless. 
                Our ultra-fine diffusion technology creates a gentle, cloud-like mist that respects 
                your skin's natural balance while ensuring complete peace of mind. 
                Take a moment to <Link to="/ProductPage" className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">learn more about our mist solution</Link> and 
                experience the emotional value of a cleaner, calmer daily practice.
              </p>

              <p className="font-default text-sm font-medium text-sage-deep/90 mt-6 max-w-xl">
                Designed with meticulous care for safety and comfort across the whole family.
              </p>

              <div className="mt-8">
                <Button asChild className="rounded-lg bg-primary text-white-pure hover:brightness-90 transition-all px-8 py-6 text-lg">
                  <Link to="/ProductPage">Explore Product Details</Link>
                </Button>
              </div>
            </div>

            <p className="font-default text-sm text-sage-deep/60 mt-14 italic">
              Designed for the most sensitive moments, our mist offers a higher standard of gentle care:
            </p>

            <div ref={statsRef} className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mt-6">
              {stats.map((stat, i) => <div key={i} data-index={i} className={`group relative h-60 p-6 md:p-7 flex flex-col justify-between border border-border bg-white rounded-lg shadow-md transition-all duration-700 ease-out hover:shadow-lg ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
              transitionDelay: statsVisible ? `${i * 120}ms` : "0ms"
            }}>
                  <div className="relative inline-block w-fit">
                    <div className="font-default font-bold text-primary text-6xl md:text-7xl leading-none transition-transform duration-300 ease-out group-hover:scale-105 origin-left">
                      {stat.value}
                    </div>
                    <span aria-hidden="true" className="absolute left-0 -bottom-2 h-px bg-primary w-0 transition-all duration-500 ease-out group-hover:w-full" />
                  </div>

                  <div>
                    <div className="font-mono text-xs uppercase tracking-[0.2em] text-sage-deep mb-2">
                      {stat.label}
                    </div>
                    <p className="font-default text-sm text-sage-deep/70 leading-snug">
                      {stat.descriptor}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}